"use client";

import NextImage from "next/image";
import { type CSSProperties, type PointerEvent, useRef, useState } from "react";
import styles from "./BeforeAfter.module.scss";

interface BeforeAfterProps {
  before: string;
  after: string;
  /** What both images show; each layer's label is appended for its alt. */
  alt: string;
  beforeLabel?: string;
  afterLabel?: string;
  caption?: string;
  /** Intrinsic size of the pair (both images share it), from the manifest. */
  width: number;
  height: number;
}

/**
 * A drag-to-compare pair for inside an MDX section: the before image sits
 * underneath, the after image is clipped from the split line rightwards.
 * Pointer drag anywhere on the frame (touch keeps vertical scroll), plus a
 * visually hidden range input so the split is reachable from the keyboard.
 */
export function BeforeAfter({
  before,
  after,
  alt,
  beforeLabel = "Before",
  afterLabel = "After",
  caption,
  width,
  height,
}: BeforeAfterProps) {
  const [split, setSplit] = useState(50);
  const dragging = useRef(false);

  const moveTo = (e: PointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * 100;
    setSplit(Math.min(100, Math.max(0, x)));
  };

  return (
    <figure className={styles.compare}>
      <div
        className={styles.frame}
        style={
          {
            aspectRatio: `${width} / ${height}`,
            "--split": `${split}%`,
          } as CSSProperties
        }
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          moveTo(e);
        }}
        onPointerMove={(e) => {
          if (dragging.current) moveTo(e);
        }}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
      >
        <NextImage
          src={before}
          alt={`${alt}, ${beforeLabel.toLowerCase()}`}
          fill
          sizes="(max-width: 960px) 100vw, 960px"
          draggable={false}
        />
        <div className={styles.after}>
          <NextImage
            src={after}
            alt={`${alt}, ${afterLabel.toLowerCase()}`}
            fill
            sizes="(max-width: 960px) 100vw, 960px"
            draggable={false}
          />
        </div>
        <span className={styles.line} aria-hidden="true">
          <span className={styles.knob} />
        </span>
        <span className={`oi-xs ${styles.tag} ${styles.tagBefore}`}>
          {beforeLabel}
        </span>
        <span className={`oi-xs ${styles.tag} ${styles.tagAfter}`}>
          {afterLabel}
        </span>
        <input
          className={styles.range}
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(split)}
          onChange={(e) => setSplit(Number(e.target.value))}
          aria-label={`Compare ${beforeLabel} and ${afterLabel}`}
        />
      </div>
      {caption && (
        <figcaption className={`oi-xs oi-muted ${styles.caption}`}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
